import React from 'react';
import FirebaseAuthContext from './FirebaseAuthContext';
import AuthDispatchContext from './AuthDispatchContext';
import AuthStateContext from './AuthStateContext';
import jwtDecode from 'jwt-decode';

import useEnvironment from 'components/Environment/useEnvironment';
import { useLocation, useHistory } from 'react-router';
import { initializeApp } from 'firebase/app';
import {
  onAuthStateChanged,
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  signInWithRedirect,
  GoogleAuthProvider,
  getRedirectResult
} from 'firebase/auth';

// (新)Google IDPによるユーザー情報、ログインに使用
function parseUser(value) {
  if (!value) {
    return {};
  }

  return {
    expired : () => {
      return false;
    },
    isValid: true,
    emailVerified: value.emailVerified,
    name: value.displayName,
    email: value.email,
    isMailed: false,
    isCustomer: false,
    isUser: true,
    isClient: true,
    isStaff: true,
    isLocked: false,
  }
}

// (旧)NDのトークン、ユーザーがスタキャスで接続した場合、PlusNaviから接続した場合に利用
function parseToken(value) {
  if (!value) {
    return {};
  }

  var decoded = jwtDecode(value);

  return {
    expired : () => {
      let expired = true;
      if (typeof decoded.exp !== 'undefined') {
        const now = Date.now().valueOf() / 1000;
        if (now < decoded.exp) {
          expired = false;
        }
      }
      return expired;
    },
    isValid: true,
    name: decoded.name,
    isMailed : decoded.type === "mailed",
    isCustomer: decoded.type === "customer",
    isUser: decoded.type === "user",
    isTemp: decoded.type === "temp",
    isClient: decoded.cat === "Client" || decoded.cat === "Both",
    isStaff: decoded.cat === "Staff" || decoded.cat === "Both",
    isLocked: Boolean(decoded.did),
    report: Number(decoded.rep),
    email: (decoded.dst) ? decoded.dst : decoded.mail,
    noLogin: decoded.type === "customer" || Boolean(decoded.did)
//        decoded : decoded,
  }
}

const initialState = {};

function initialize() {
  return {
    token: sessionStorage.getItem('token'),
    auth: parseToken(sessionStorage.getItem('token'))
  };
}

function reducer(state, action) {
  switch(action.type) {
    case "INIT": {
      return {
        ...state,
        fbAuth: action.value
      };
    }
    case "TOKEN": {
      // TokenをParseした際に呼ばれる
      sessionStorage.setItem('token', action.value);
      return {
        ...state,
        token: action.value,
        auth: parseToken(action.value)
      };
    }
    case "VOID_TOKEN": {
      // ログインボタン押下時、ログイン済みの場合は破棄するためのイベントを発行する。
      sessionStorage.removeItem('token');
      return {
        ...state,
        token: null,
        auth: parseUser(state.user)
      };
    }
    case "SIGNIN": {
      // IDPのログインはキャッシュされる
      // SIGNINイベントはキャッシュからロードされた時点で発生する
      const token = sessionStorage.getItem('token');
      return {
        ...state,
        user: action.value,
        // 資格情報はパーストークンを優先
        auth: (token) ? parseToken(state.token) : parseUser(action.value),
      };
    }
    case "SIGNOUT": {
      // ログアウト押下時の動作
      signOut(state.fbAuth);
      return {
        ...state,
        auth: {}
      };
    }
    default:
      return state;
  }
}

// dispatchを利用した関数群を返す
function createFunctions(dispatch)
{
  return {
    init: (auth) => {
      dispatch({type:"INIT", value:auth})
    },
    setToken: (token) => {
      dispatch({type:"TOKEN", value: token})
    },
    removeToken: () => {
      dispatch({type:"VOID_TOKEN"});
    },
    signIn : (user) => {
      dispatch({type:"SIGNIN", value: user});
    },
    signOut : () => {
      dispatch({type:"SIGNOUT"});
    }
  }
}

export default function AuthProvider(props)
{
  const [fbAuth, setFbAuth] = React.useState();
  const [fbInitialized, setFbInitialized] = React.useState(false);
  const [state, dispatch] = React.useReducer(reducer, initialState, initialize);
  const functions = React.useMemo(() => createFunctions(dispatch), [dispatch]);

  const environment = useEnvironment();
  // Google IDPの有効化/ページ到達時の再ログイン
  React.useEffect(() => {
    const app = initializeApp(
    {
      apiKey : environment.firebaseConfig.apiKey,
      authDomain: environment.firebaseConfig.authDomain,
    });
    
    const auth = getAuth(app);
    auth.languageCode = 'ja';
    if (environment.firebaseConfig.tenantId) {
      auth.tenantId = environment.firebaseConfig.tenantId;
    }

    setFbAuth(auth);
    functions.init(auth);

    // NOTE:getRedirectResultについて
    // signInWithRedirectを使った場合、結果をチェックすることでログインができるようになる
    // AuthProviderはページアクセス時に1回だけuseEffectをトリガーする（ページ遷移等ではトリガーされない）
    // そのため、1度だけ呼び出しておく（このメソッドは非同期）
    getRedirectResult(auth);

    // onAuthStateChangedは、FirebaseのAuthに対する変更を確認して、通知してくれるイベント。
    // ログインメソッドを呼び出した後、このイベントを経由してdispatchイベントにログインユーザー情報が送られる。
    // 戻り値としてイベントの購読解除メソッドが返されるため、useEffectに戻しておく。
    return onAuthStateChanged(auth, (user) => {
      functions.signIn(user)
      setFbInitialized(true);
    }); 
  }, []);

  // トークンをURLからパースする場合のハッシュ値の読み取り
  const [hashInitialized, setHashInitialized] = React.useState(false);
  const location = useLocation();
  const history = useHistory();
  React.useEffect(() => {
    const hash = location && location.hash;
    if (hash) {
        var parsedToken = hash.substring(1);
        functions.setToken(parsedToken);
        history.push(`${location.pathname}${location.search}`);
    }
    setHashInitialized(true);
}, []);


  return (
    <FirebaseAuthContext.Provider value={fbAuth}>
      <AuthDispatchContext.Provider value={functions}>
        <AuthStateContext.Provider value={state}>
          {fbInitialized && hashInitialized ? props.children : null}
        </AuthStateContext.Provider>
      </AuthDispatchContext.Provider>
    </FirebaseAuthContext.Provider>
  );
}